import type * as echarts from 'echarts';
import type { LabelValueOption } from '../shared/self-service-config.types';
import type { ComputedColumn } from '../shared/self-service-types';

/**
 * Shared types of the DVM layout builder — the shell component, its step
 * components (self-service-visualization/components/*) and the published
 * dashboard all read these. Kept free of Angular imports on purpose.
 */

export type BridgeStepType = 'START' | 'CHANGE' | 'TOTAL';
export type BridgeSign = 'AUTO' | 'POSITIVE' | 'NEGATIVE';

export interface TableTypeOption extends LabelValueOption {
  group?: string;
  hint?: string;
  normal?: boolean;
  editable?: boolean;
  drilldown?: boolean;
  [flag: string]: unknown;
}

export interface BridgeStep {
  id: string;
  stepType: BridgeStepType;
  label: string;
  dataset: string;
  field: string;
  aggregation: string;
  sign: BridgeSign;
  formula: string;
  formulaMode?: boolean;
}

export interface TargetFormulaReferenceEntry {
  token: string;
  inputTitle: string;
  targetLabel: string;
  rowLabel: string;
  step?: string;
}

export interface PayloadTemplateKvRow {
  key: string;
  value: string;
}
export interface PayloadTemplateEndpointRow {
  functionName: string;
  version: string;
}
export interface PayloadTemplateDraft {
  dataKey: string;
  includeColumns: string[];
  staticValues: PayloadTemplateKvRow[];
  endpoints: PayloadTemplateEndpointRow[];
  extraFields: PayloadTemplateKvRow[];
}

export interface WaterfallBar {
  label: string;
  delta: number;
  bottom: number;
  top: number;
  type: string;
  colorSign?: string;
}

export interface DrillDownDimension {
  id: string;
  field: string;
  label: string;
  valueOrder?: string;
  editable?: boolean;
  editor?: 'dropdown';
  dropdownOptions?: string;
  dropdownFromData?: boolean;
  dataset?: string;
}

export interface DrillColumnOrderItem {
  key: string;
  orderKey: string;
  kind: 'measure' | 'formula';
  split: boolean;
}

export interface DrillComputedColumn extends ComputedColumn {
  after?: string;
}

export interface TmColumnDef {
  id: string;
  dataset: string;
  field: string;
  displayName: string;
  editable?: boolean;
  control?: string;
  hidden?: boolean;
  saveAllTrigger?: boolean;
  mandatory?: boolean;
  isAction?: boolean;
  buttonIcon?: string;
  buttonLabel?: string;
  actionKind?: 'api' | 'rowPopup' | 'apiPopup' | 'popupComponent' | 'emit';
  actionUrl?: string;
  actionMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  actionBody?: string;
  popupComponentId?: string;
  actionRowsPath?: string;
  actionRowsSortBy?: string;
  actionRowsSortDir?: 'asc' | 'desc';
  actionMaxRows?: number;
  actionRowsColumns?: string;
  actionTitle?: string;
  actionConfirm?: string;
  actionRefresh?: boolean;
  align: '' | 'start' | 'center' | 'end';
  filterField: string;
  filterOperator: string;
  filterValue: string;
}

export interface DrillDownMeasure {
  id: string;
  field: string;
  label: string;
  aggregation: string;
  rollUpStrategy: string;
  total: boolean;
  editable?: boolean;
  editableLevels?: number[];
  editor?: 'dropdown';
  dropdownOptions?: string;
  dropdownFromData?: boolean;
  dataset?: string;
  pivot?: boolean;
  kind?: 'number' | 'text';
  hidden?: boolean;
  rowTotalHeader?: string;
  yearScope?: 'latest';
  yearField?: string;
  showYearColumn?: boolean;
}

export interface DrillYearHelperMeasure {
  field: string;
  label: string;
  aggregation: string;
  rollUpStrategy: string;
  total: boolean;
  hidden: boolean;
  yearHelper: true;
  dataset?: string;
  pivot?: false;
  kind?: 'text';
  yearScope?: 'latest';
  yearField?: string;
}

export interface BarBuilderMeasure {
  id: string;
  field: string;
  label: string;
  renderAs: 'bar' | 'line' | 'area';
  axis: 'y1' | 'y2';
}

export interface LineAreaBuilderMeasure {
  id: string;
  field: string;
  label: string;
  renderAs: 'line' | 'area';
  axis: 'y1' | 'y2';
  color?: string;
  symbol?: string;
  symbolSize?: number;
}

export interface ClientChartCrossFilterSpec {
  dataField: keyof DashboardTile;
  rowsField: keyof DashboardTile;
}

export const CLIENT_CHART_CROSS_FILTER: Record<string, ClientChartCrossFilterSpec> = {
  BAR: { dataField: 'barData', rowsField: 'barRows' },
  SCATTER_BUBBLE: { dataField: 'scatterBubbleData', rowsField: 'scatterBubbleRows' },
  LINE_AREA: { dataField: 'lineAreaData', rowsField: 'lineAreaRows' },
};

export interface DashboardTile {
  id: string;
  componentId: string;
  layoutType: string;
  title: string;
  colSpan: number;
  rowSpan: number;
  x?: number;
  y?: number;
  customSize?: boolean;
  payload: Record<string, unknown>;
  previewOption?: echarts.EChartsOption | null;
  waterfallData?: Record<string, unknown> | null;
  drillData?: Record<string, unknown> | null;
  hbData?: Record<string, unknown> | null;
  kpiData?: Record<string, unknown> | null;
  barData?: Record<string, unknown> | null;
  barRows?: Array<Record<string, unknown>> | null;
  scatterBubbleData?: Record<string, unknown> | null;
  scatterBubbleRows?: Array<Record<string, unknown>> | null;
  lineAreaData?: Record<string, unknown> | null;
  lineAreaRows?: Array<Record<string, unknown>> | null;
  tableMatrixData?: Record<string, unknown> | null;
  inputControl?: DashboardTileInput | null;
  dropdownControl?: DashboardTileDropdown | null;
  fileUploadControl?: DashboardTileFileUpload | null;
  popupControl?: DashboardTilePopup | null;
  format?: Record<string, unknown>;
  waterfallBars?: WaterfallBar[];
  waterfallBarsAll?: WaterfallBar[];
  filterKey?: string;
  listenerTileIds?: string[];
  groupTitle?: string;
  groupMembers?: DashboardTile[];
  groupColumns?: number;
}

export interface PublishedLayoutSnapshotItem {
  id: string;
  componentId: string;
  colSpan: number;
  rowSpan: number;
  x: number;
  y: number;
  order: number;
  customSize: boolean;
}

export interface InputApiCallConfig {
  enabled: boolean;
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH';
  bodyTemplate: string;
  mode: 'replace' | 'parallel';
  targets: string[];
}

export interface DashboardTileInput {
  kind: 'INPUT_BOX' | 'SLIDER';
  formula: string;
  value: number | string | null;
  targets: string[];
  widgetData: Record<string, unknown>;
  apiConfig?: InputApiCallConfig;
}

export interface DashboardTileDropdown {
  field: string;
  options: string[];
  value: string[];
  targets: string[];
  widgetData: Record<string, unknown>;
  apiConfig?: InputApiCallConfig;
}

export interface DashboardTileFileUpload {
  targets: string[];
  buttonLabel: string;
  accept: string;
  widgetData: Record<string, unknown>;
  apiConfig?: InputApiCallConfig;
}

export interface DashboardTilePopup {
  widgetData: Record<string, unknown>;
}

export interface PopupBlockForm {
  id: number;
  type: 'heading' | 'text' | 'keyValue' | 'table' | 'apiTable' | 'buttons' | 'divider';
  text: string;
  level: string;
  align: string;
  pairs: Array<{ label: string; value: string }>;
  dataset: string;
  columns: string[];
  maxRows: number | null;
  url: string;
  method: string;
  body: string;
  rowsPath: string;
  groupsPath: string;
  groupRowsField: string;
  metaFields: Array<{ field: string; label: string }>;
  apiColumns: Array<{ field: string; label: string; color: string }>;
  refreshOnOpen: boolean;
  buttons: Array<{ label: string; action: string; url: string; targetDashboard: string; style: string }>;
}
